import { auth } from '../firebase';
import API_BASE_URL from '../config/api_config';

/**
 * Generic function to make unauthenticated API calls with timeout
 * @param {string} endpoint - API endpoint (without /api prefix)
 * @param {Object} options - Fetch options
 * @param {number} timeoutMs - Timeout in milliseconds (default: 30000)
 * @returns {Promise} - API response
 */
async function makeUnauthenticatedRequest(endpoint, options = {}, timeoutMs = 30000) {
  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        ...(options.headers || {}),
        'Content-Type': 'application/json',
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs}ms`);
    }
    
    throw error;
  }
}

/**
 * Generic function to make authenticated API calls with timeout
 * @param {string} endpoint - API endpoint (without /api prefix)
 * @param {Object} options - Fetch options
 * @param {number} timeoutMs - Timeout in milliseconds (default: 30000)
 * @returns {Promise} - API response
 */
async function makeAuthenticatedRequest(endpoint, options = {}, timeoutMs = 30000) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated");
  }

  const token = await user.getIdToken();
  
  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        ...(options.headers || {}),
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs}ms`);
    }
    
    throw error;
  }
}

/**
 * Clip Audio API functions - Based on real usage in your components
 */
export const clipAudioApi = {
  /**
   * Get all clip audio projects
   * Used in: ClipAudioProjectsScreen.js
   * @returns {Promise<Array>} - Array of projects
   */
  async getAllProjects() {
    return makeAuthenticatedRequest('clip_audio');
  },

  /**
   * Get a specific clip audio project by ID
   * Used in: ClipAudioPipelineScreen.js, ClipAudioDetailsScreen.js
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} - Project data
   */
  async getProject(projectId) {
    return makeAuthenticatedRequest(`clip_audio/${projectId}`);
  },

  /**
   * Create a new clip audio project (basic version)
   * Used in: ClipAudioProjectsScreen.js - handleCreateProject
   * @param {string} projectName - Project name
   * @param {string} theme - Project theme (optional)
   * @param {string} style - Visual style (optional)
   * @returns {Promise<Object>} - Created project
   */
  async createProject(projectName, theme = "my_chosen_theme", style = "cartoon", image_format = "16:9") {
    return makeAuthenticatedRequest('clip_audio/', {
      method: 'POST',
      body: JSON.stringify({
        name: projectName,
        theme: theme,
        style: style,
        image_format: image_format
      })
    });
  },

  /**
   * Create a new clip audio project (advanced version with lyrics)
   * Used in: ClipAudioProjectsScreen.js - handleCreateProjectWithLyrics
   * @param {string} projectName - Project name
   * @param {string} theme - Project theme (optional)
   * @param {string} lyrics - Project lyrics (optional)
   * @param {string} style - Visual style (optional)
   * @returns {Promise<Object>} - Created project
   */
  async createProjectWithLyrics(projectName, theme = "", lyrics = "", style = "cartoon", image_format = "16:9") {
    return makeAuthenticatedRequest('clip_audio/', {
      method: 'POST',
      body: JSON.stringify({
        name: projectName,
        theme: theme,
        lyrics: lyrics,
        style: style,
        image_format: image_format
      })
    });
  },

  /**
   * Execute a step in a clip audio project
   * Used in: All Step components (Step1ThemePrompt.js, Step2SceneGeneration.js, Step3ImageGeneration.js, Step4Animation.js)
   * Used in: ClipAudioDetailsScreen.js - handleStepGenerate
   * @param {string} projectId - Project ID
   * @param {number} stepNumber - Step number to execute
   * @param {Object} stepData - Additional step data (optional)
   * @returns {Promise<Object>} - Execution result
   */
  async executeStep(projectId, stepNumber, stepData = {}) {
    return makeAuthenticatedRequest(`clip_audio/execute_step/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({
        stepNumber: stepNumber,
        stepData: {
          forceRegenerate: true,
          ...stepData
        }
      })
    }, 180000); // 3 minutes timeout for image generation
  },

  /**
   * Validate a step in a clip audio project
   * Used in: All Step components (Step1ThemePrompt.js, Step2SceneGeneration.js, Step3ImageGeneration.js, Step4Animation.js)
   * Used in: ClipAudioDetailsScreen.js - handleStepValidate
   * @param {string} projectId - Project ID
   * @param {number} stepNumber - Step number to validate
   * @param {Object} stepData - Additional step data (optional)
   * @returns {Promise<Object>} - Validation result
   */
  async validateStep(projectId, stepNumber, stepData = {}) {
    return makeAuthenticatedRequest(`clip_audio/validate_step/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({
        stepNumber: stepNumber,
        stepData: stepData
      })
    });
  },

  /**
   * Update an image in a clip audio project (from Step3ImageGeneration.js)
   * Used in: Step3ImageGeneration.js - handleRegenerateImage
   * @param {string} projectId - Project ID
   * @param {string} imageUrl - Image URL to update
   * @param {number} sceneId - Scene ID
   * @param {string} prompt - Regeneration prompt
   * @returns {Promise<Object>} - Update result
   */
  async updateImageFromStep3(projectId, imageUrl, sceneId, prompt) {
    return makeAuthenticatedRequest(`clip_audio/update_image/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({
        image_url: imageUrl,
        scene_id: sceneId,
        prompt: prompt
      })
    }, 180000); // 3 minutes timeout for image regeneration
  },

  /**
   * Update an image in a clip audio project (from ClipAudioDetailsScreen.js)
   * Used in: ClipAudioDetailsScreen.js - handleRegenerateImage
   * @param {string} projectId - Project ID
   * @param {string} imageUrl - Image URL to update
   * @param {number} sceneId - Scene ID
   * @param {string} prompt - Regeneration prompt
   * @param {string} model - Optional model ID for regeneration
   * @returns {Promise<Object>} - Update result
   */
  async updateImageFromDetails(projectId, imageUrl, sceneId, prompt, model = null) {
    const requestBody = {
      image_url: imageUrl,
      scene_id: sceneId,
      prompt: prompt
    };
    
    // Include model if provided
    if (model) {
      requestBody.model = model;
    }
    
    return makeAuthenticatedRequest(`clip_audio/update_image/${projectId}`, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    }, 180000); // 3 minutes timeout for image regeneration
  },

  /**
   * Regenerate an image angle in a clip audio project
   * @param {string} projectId - Project ID
   * @param {number} sceneId - Scene ID
   * @param {number} imageIndex - Index of the selected image within the scene
   * @param {string} prompt - Regeneration prompt
   * @param {string} model - Model ID to use
   * @returns {Promise<Object>} - Update result
   */
  async updateImageAngle(projectId, sceneId, imageIndex, prompt, model) {
    return makeAuthenticatedRequest(`clip_audio/update_image_angle/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({
        scene_id: sceneId,
        image_index: imageIndex,
        prompt: prompt,
        model: model
      })
    }, 180000);
  },

  /**
   * Resize an image in a clip audio project
   * Used in: ClipAudioDetailsScreen.js - handleResizeImage
   * @param {string} projectId - Project ID
   * @param {string} imageUrl - Image URL to resize
   * @param {number} sceneId - Scene ID
   * @returns {Promise<Object>} - Resize result
   */
  async resizeImage(projectId, imageUrl, sceneId) {
    return makeAuthenticatedRequest(`clip_audio/resize_image/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({
        image_url: imageUrl,
        scene_id: sceneId
      })
    }, 180000); // 2 minutes timeout for image resizing
  },

  /**
   * Delete a clip audio project
   * @param {string} projectId - Project ID to delete
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteProject(projectId) {
    return makeAuthenticatedRequest(`clip_audio/${projectId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Update images selection for a clip audio project
   * @param {string} projectId - Project ID
   * @param {Array} images - The entire images array from workflow_data with updated selected flags
   * @returns {Promise<Object>} - Update result
   */
  async updateImagesSelection(projectId, images) {
    return makeAuthenticatedRequest(`clip_audio/${projectId}/images`, {
      method: 'PUT',
      body: JSON.stringify({
        images: images
      })
    });
  }
};

/**
 * Image AI API functions
 */
export const imageApi = {
  /**
   * Edit an image using AI
   * Used in: ImageFromTextImage.js
   * @param {string} prompt - Text prompt for image editing
   * @param {Array<string>} imagesInput - Array of base64 images
   * @returns {Promise<Object>} - Edit result
   */
  async editImage(prompt, imagesInput) {
    return makeAuthenticatedRequest('ia/edit-image', {
      method: 'POST',
      body: JSON.stringify({
        prompt: prompt,
        images_input: imagesInput
      })
    });
  }
};

/**
 * AI Models API functions
 */
export const aiModelsApi = {
  /**
   * Get AI models by type
   * @param {string} modelType - Type of models to fetch (e.g., "image_generation_models")
   * @returns {Promise<Array>} - Array of available models
   */
  async getAIModels(type_model){
    try {
      const response = await makeAuthenticatedRequest(`/ia/models/${type_model}`);
      
      // Handle the specific response structure
      if (response.success && Array.isArray(response.data)) {
        // Transform the string array into model objects with id and name
        return response.data.map(modelId => ({
          id: modelId,
          name: modelId.split('/').pop(), // Extract model name from "provider/model-name" format
          fullName: modelId
        }));
      } else {
        throw new Error('Invalid response structure from AI models API');
      }
    } catch (error) {
      console.error('Error fetching AI models:', error);
      throw error;
    }
  }
};

/**
 * Seance API functions
 */
export const seanceApi = {
  /**
   * Get all seances
   * @returns {Promise<Object>} - Seances data with pagination
   */
  async getAllSeances() {
    return makeAuthenticatedRequest('seances');
  },

  /**
   * Get a specific seance by ID
   * @param {string} seanceId - The seance ID
   * @returns {Promise<Object>} - Seance data
   */
  async getSeanceById(seanceId) {
    return makeAuthenticatedRequest(`seances/${seanceId}`);
  },

  /**
   * Get a specific seance by ID (public, no authentication required)
   * @param {string} seanceId - The seance ID
   * @returns {Promise<Object>} - Seance data
   */
  async getPublicSeanceById(seanceId) {
    return makeUnauthenticatedRequest(`public/seances/${seanceId}`);
  },

  /**
   * Create a seance from a prompt
   * @param {string} prompt - The prompt for creating the seance
   * @param {Object} formData - Additional form data (optional)
   * @param {string} mode - Creation mode ('prompt_only' or 'form_prompt')
   * @returns {Promise<Object>} - Created seance data
   */
  async createSeanceFromPrompt(prompt, formData = {}, mode = 'prompt_only') {
    return makeAuthenticatedRequest('generation/seance', {
      method: 'POST',
      body: JSON.stringify({
        mode: mode,
        prompt: prompt,
        form_data: formData
      })
    }, 180000); // 3 minutes timeout for seance creation
  },

  /**
   * Update a seance with a new prompt
   * @param {string} seanceId - The seance ID
   * @param {string} prompt - The prompt for regenerating the seance
   * @returns {Promise<Object>} - Updated seance data
   */
  async updateSeanceWithPrompt(seanceId, prompt) {
    return makeAuthenticatedRequest(`generation/seance/${seanceId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        prompt: prompt,
        seanceId: seanceId
      })
    }, 180000); // 3 minutes timeout for regeneration
  },

  /**
   * Update seance data with PUT request
   * @param {string} seanceId - The seance ID
   * @param {Object} seanceData - The updated seance data
   * @returns {Promise<Object>} - Updated seance data
   */
  async updateSeanceData(seanceId, seanceData) {
    return makeAuthenticatedRequest(`generation/seance/${seanceId}`, {
      method: 'PUT',
      body: JSON.stringify(seanceData)
    });
  },

  /**
   * Modify a specific bloc with AI prompt
   * @param {string} seanceId - The seance ID
   * @param {number} blocIndex - The bloc index
   * @param {number} sequenceIndex - The sequence index
   * @param {string} prompt - The modification prompt
   * @returns {Promise<Object>} - Modified bloc data
   */
  async modifyBloc(seanceId, blocIndex, sequenceIndex, prompt) {
    return makeAuthenticatedRequest(`generation/seance/${seanceId}/blocks/modify`, {
      method: 'PATCH',
      body: JSON.stringify({
        bloc_index: blocIndex,
        sequence_index: sequenceIndex,
        prompt: prompt
      })
    }, 180000); // 3 minutes timeout for bloc modification
  },

  /**
   * Generate images for a specific block
   * @param {string} seanceId - The seance ID
   * @param {number} blocIndex - The bloc index
   * @param {number} sequenceIndex - The sequence index
   * @param {string} prompt - The image generation prompt
   * @param {number} number_of_images - Number of images to generate (default: 1)
   * @param {string} style - Image style (default: "cartoon")
   * @param {string} model - AI model to use (default: "google/gemini-2.5-flash-image")
   * @param {string} aspectRatio - Aspect ratio (default: "16:9")
   * @returns {Promise<Object>} - Generated images data
   */
  async generateBlockImages(seanceId, blocIndex, sequenceIndex, prompt, number_of_images = 1, style = "cartoon", model = "google/gemini-2.5-flash-image", aspectRatio = "16:9") {
    return makeAuthenticatedRequest(`generation/seance/${seanceId}/block/generate_images`, {
      method: 'POST',
      body: JSON.stringify({
        bloc_index: blocIndex,
        sequence_index: sequenceIndex,
        prompt: prompt,
        number_of_images: number_of_images,
        style: style,
        model: model,
        aspectRatio: aspectRatio
      })
    }, 180000); // 3 minutes timeout for image generation
  },

  /**
   * Update/regenerate a specific image in a block
   * @param {string} seanceId - The seance ID
   * @param {number} blocIndex - The bloc index
   * @param {number} sequenceIndex - The sequence index
   * @param {number} imageIndex - The image index to regenerate
   * @param {string} prompt - The regeneration prompt
   * @param {string} imageFormat - Image format/aspect ratio (default: "16:9")
   * @returns {Promise<Object>} - Updated image data
   */
  async updateImageBlock(seanceId, blocIndex, sequenceIndex, imageIndex, prompt, imageFormat = "16:9", model = null) {
    const payload = {
      bloc_index: blocIndex,
      sequence_index: sequenceIndex,
      image_index: imageIndex,
      prompt: prompt,
      imageFormat: imageFormat
    };
    
    // Add model to payload if provided
    if (model) {
      payload.model = model;
    }
    
    return makeAuthenticatedRequest(`generation/seance/${seanceId}/blocks/update_image`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    }, 180000); // 3 minutes timeout for image regeneration
  },

  /**
   * Generate images for a sequence (creates an image block)
   * @param {string} seanceId - The seance ID
   * @param {number} sequenceIndex - The sequence index
   * @param {string} prompt - The generation prompt
   * @param {number} number_of_images - Number of images to generate (default: 1)
   * @param {string} style - Image style (default: "cartoon")
   * @param {string} model - AI model to use (default: "google/gemini-2.5-flash-image")
   * @param {string} aspectRatio - Aspect ratio (default: "16:9")
   * @returns {Promise<Object>} - Generated images data
   */
  async generateImagesForSequence(seanceId, sequenceIndex, prompt, number_of_images = 1, style = "cartoon", model = "google/gemini-2.5-flash-image", aspectRatio = "16:9") {
    return makeAuthenticatedRequest(`generation/seance/${seanceId}/sequence/${sequenceIndex}/generate_images`, {
      method: 'POST',
      body: JSON.stringify({
        prompt: prompt,
        number_of_images: number_of_images,
        style: style,
        model: model,
        aspectRatio: aspectRatio
      })
    }, 180000); // 3 minutes timeout for image generation
  },

  /**
   * Delete a seance
   * @param {string} seanceId - The seance ID to delete
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteSeance(seanceId) {
    return makeAuthenticatedRequest(`seances/${seanceId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Duplicate a seance
   * @param {string} seanceId - The seance ID to duplicate
   * @returns {Promise<Object>} - Duplication result with duplicate_seance_id
   */
  async duplicateSeance(seanceId) {
    return makeAuthenticatedRequest(`seances/${seanceId}/duplicate`, {
      method: 'POST'
    });
  },

  /**
   * Translate a seance
   * @param {string} seanceId - The seance ID to translate
   * @param {string} language - The target language (french, italian, hebrew, english)
   * @returns {Promise<Object>} - Translation result
   */
  async translateSeance(seanceId, language) {
    return makeAuthenticatedRequest(`seances/${seanceId}/translate`, {
      method: 'PUT',
      body: JSON.stringify({
        language: language
      })
    }, 180000); // 3 minutes timeout for seance translation
  },

  /**
   * Upload an image to a block
   * @param {string} seanceId - The seance ID
   * @param {number} sequenceIndex - The sequence index
   * @param {number} blockIndex - The block index
   * @param {File} imageFile - The image file to upload
   * @returns {Promise<Object>} - Upload result
   */
  async uploadImageBlock(seanceId, sequenceIndex, blockIndex, imageFile) {
    // Convert file to base64 data URL (keep the full data URL format)
    const base64DataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Keep the full data URL format (e.g., "data:image/png;base64,iVBORw0KG...")
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    });

    return makeAuthenticatedRequest(`seances/${seanceId}/upload-image-block`, {
      method: 'PUT',
      body: JSON.stringify({
        sequenceIndex: sequenceIndex,
        blockIndex: blockIndex,
        image: base64DataUrl
      })
    }, 180000); // 3 minutes timeout for image upload
  },

  /**
   * Upload an image to a block from a URL
   * @param {string} seanceId - The seance ID
   * @param {number} sequenceIndex - The sequence index
   * @param {number} blockIndex - The block index
   * @param {string} imageUrl - The image URL
   * @param {string} title - The image title (optional)
   * @returns {Promise<Object>} - Upload result
   */
  async uploadImageBlockFromUrl(seanceId, sequenceIndex, blockIndex, imageUrl, title = null) {
    return makeAuthenticatedRequest(`seances/${seanceId}/upload-image-block`, {
      method: 'PUT',
      body: JSON.stringify({
        sequenceIndex: sequenceIndex,
        blockIndex: blockIndex,
        imageUrl: imageUrl,
        title: title
      })
    }, 180000); // 3 minutes timeout for image upload
  },

  /**
   * Set thumbnail for a seance
   * @param {string} seanceId - The seance ID
   * @param {Object} imageObject - The image object to set as thumbnail (can be image object, base64 string, or image URL)
   * @returns {Promise<Object>} - Update result
   */
  async setThumbnail(seanceId, imageObject) {
    return makeAuthenticatedRequest(`seances/${seanceId}/thumbnail`, {
      method: 'PUT',
      body: JSON.stringify({
        image: imageObject
      })
    });
  }
};

/**
 * Music API functions
 */
export const musicApi = {
  /**
   * Get all musics
   * @returns {Promise<Object>} - Musics data with count
   */
  async getAllMusics() {
    return makeAuthenticatedRequest('galeries/music');
  },

  /**
   * Delete a music by ID
   * @param {string} musicId - The music ID to delete
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteMusic(musicId) {
    return makeAuthenticatedRequest(`galeries/music/${musicId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Upload a new music file
   * @param {string} name - Music name
   * @param {string} fileBase64 - Base64 encoded MP3 file
   * @returns {Promise<Object>} - Upload result
   */
  async uploadMusic(name, fileBase64) {
    return makeAuthenticatedRequest('galeries/music', {
      method: 'POST',
      body: JSON.stringify({
        name: name,
        file: fileBase64
      })
    });
  }
};

export const galleryImageApi = {
  /**
   * Get all images from gallery
   * @returns {Promise<Object>} - Images data with count
   */
  async getAllImages() {
    return makeAuthenticatedRequest('galeries/images');
  },

  /**
   * Delete an image by ID
   * @param {string} imageId - The image ID to delete
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteImage(imageId) {
    return makeAuthenticatedRequest(`galeries/images/${imageId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Upload a new image from file or URL
   * @param {string} name - Image name
   * @param {string} fileBase64 - Base64 encoded image file (optional)
   * @param {string} url - Image URL (optional)
   * @returns {Promise<Object>} - Upload result
   */
  async uploadImage(name, fileBase64 = null, url = null) {
    const body = { name };
    if (fileBase64) {
      body.file = fileBase64;
    }
    if (url) {
      body.url = url;
    }
    return makeAuthenticatedRequest('galeries/images', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  /**
   * Add an image to gallery from URL
   * @param {string} url - Image URL
   * @param {string|null} name - Image name/title (can be null)
   * @returns {Promise<Object>} - Upload result
   */
  async addImageToGallery(url, name = null) {
    return makeAuthenticatedRequest('galeries/images', {
      method: 'POST',
      body: JSON.stringify({ url, name })
    });
  }
};

/**
 * Generic API functions for custom endpoints
 */
export const api = {
  /**
   * Make a custom authenticated API call
   * @param {string} endpoint - API endpoint (without /api prefix)
   * @param {Object} options - Fetch options
   * @returns {Promise} - API response
   */
  async customRequest(endpoint, options = {}) {
    return makeAuthenticatedRequest(endpoint, options);
  },

  /**
   * Make a GET request to any endpoint
   * @param {string} endpoint - API endpoint (without /api prefix)
   * @param {Object} params - Query parameters
   * @returns {Promise} - API response
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;
    return makeAuthenticatedRequest(fullEndpoint, { method: 'GET' });
  },

  /**
   * Make a POST request to any endpoint
   * @param {string} endpoint - API endpoint (without /api prefix)
   * @param {Object} data - Request body data
   * @returns {Promise} - API response
   */
  async post(endpoint, data = {}) {
    return makeAuthenticatedRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * Make a PUT request to any endpoint
   * @param {string} endpoint - API endpoint (without /api prefix)
   * @param {Object} data - Request body data
   * @returns {Promise} - API response
   */
  async put(endpoint, data = {}) {
    return makeAuthenticatedRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Make a DELETE request to any endpoint
   * @param {string} endpoint - API endpoint (without /api prefix)
   * @returns {Promise} - API response
   */
  async delete(endpoint) {
    return makeAuthenticatedRequest(endpoint, { method: 'DELETE' });
  }
};

// Export the main API object with all functions
export default {
  clipAudio: clipAudioApi,
  image: imageApi,
  aiModels: aiModelsApi,
  seance: seanceApi,
  music: musicApi,
  galleryImage: galleryImageApi,
  ...api
};